import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { getAllCategory } from '../actions'
import { NavLink, useNavigate, useParams } from 'react-router-dom'
import { getCategoryDetailsById } from './../actions/category.action'
import { setCategoryDetails } from '../reducers/category.reducer'
import CategoryList from './../containers/Category/CategoryList'
import { generatePublicUrl } from '../urlConfig'

const ACTIVE_CAT_KEY = 'ACTIVE_CAT_SLUG'

const normalizeSearchValue = (value = '') =>
    String(value).trim().toLocaleLowerCase('ru-RU')

const filterCategoryTree = (categories = [], query = '', ancestorMatched = false) => {
    if (!query) return categories

    return categories.reduce((result, category) => {
        const children = Array.isArray(category.children) ? category.children : []
        const selfMatched = normalizeSearchValue(category.name).includes(query)
        const keepWholeBranch = ancestorMatched || selfMatched

        const filteredChildren = keepWholeBranch
            ? children
            : filterCategoryTree(children, query, false)

        if (selfMatched || ancestorMatched || filteredChildren.length > 0) {
            result.push({
                ...category,
                children: keepWholeBranch ? children : filteredChildren,
            })
        }

        return result
    }, [])
}

const countCategories = (categories = []) =>
    categories.reduce(
        (total, category) =>
            total + 1 + countCategories(Array.isArray(category.children) ? category.children : []),
        0
    )

const countSelectableCategories = (categories = []) =>
    categories.reduce((total, category) => {
        const children = Array.isArray(category.children) ? category.children : []

        return total + (category.parentId ? 1 : 0) + countSelectableCategories(children)
    }, 0)

const collectSelectableCategories = (categories = [], result = []) => {
    categories.forEach((category) => {
        const children = Array.isArray(category.children) ? category.children : []

        if (category.parentId) {
            result.push(category)
        }

        if (children.length > 0) {
            collectSelectableCategories(children, result)
        }
    })

    return result
}

const CategoryContainer = ({
    setShowActiveCategory,
    showAllProductsButton,
    setCategoryItem,
    variantRender,
    setPageNumber,
    categoryItem,
    slug,
    collapsed: collapsedProp,
    onCollapsedChange,
    hideToggle = false,
}) => {
    const category = useSelector((state) => state.category?.categories)
    const { visibleHiddenCategories } = useSelector((state) => state.category)

    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { slug: slugParam } = useParams()

    const [internalCollapsed, setInternalCollapsed] = useState(true)
    const [categorySearch, setCategorySearch] = useState('')

    const collapsed = typeof collapsedProp === 'boolean'
        ? collapsedProp
        : internalCollapsed

    const setCollapsed = (nextValue) => {
        const resolvedValue = typeof nextValue === 'function'
            ? nextValue(collapsed)
            : nextValue

        if (typeof collapsedProp !== 'boolean') {
            setInternalCollapsed(resolvedValue)
        }

        if (typeof onCollapsedChange === 'function') {
            onCollapsedChange(resolvedValue)
        }
    }

    useEffect(() => {
        dispatch(getAllCategory())
    }, [visibleHiddenCategories, dispatch])

    useEffect(() => {
        const data = window.localStorage.getItem('TEST')

        if (!data) {
            setCategoryItem?.(null)
            return
        }

        try {
            setCategoryItem?.(JSON.parse(data))
        } catch {
            setCategoryItem?.(null)
        }
    }, [setCategoryItem])

    useEffect(() => {
        if (slugParam) {
            localStorage.setItem(ACTIVE_CAT_KEY, slugParam)
        }
    }, [slugParam])

    const searchQuery = useMemo(
        () => normalizeSearchValue(categorySearch),
        [categorySearch]
    )

    const filteredCategories = useMemo(
        () => filterCategoryTree(category || [], searchQuery),
        [category, searchQuery]
    )

    const totalCount = useMemo(
        () => countCategories(category || []),
        [category]
    )

    const visibleSelectableCount = useMemo(
        () => countSelectableCategories(filteredCategories),
        [filteredCategories]
    )

    if (!category) return null

    const activeSlug = slugParam || slug || localStorage.getItem(ACTIVE_CAT_KEY) || ''


    const editChildrenCategoryIndex = (categoryItemValue) => {
        setCategoryItem?.(categoryItemValue)
        localStorage.setItem(ACTIVE_CAT_KEY, categoryItemValue.slug || '')
        dispatch(setCategoryDetails(categoryItemValue))
        dispatch(getCategoryDetailsById(categoryItemValue))
        navigate(`/products/${categoryItemValue.slug}`)
    }

    const handleShowAll = () => {
        setCategorySearch('')

        if (typeof showAllProductsButton === 'function') {
            showAllProductsButton()
        }
    }

    const renderProductCategories = (categories) => {
        const selectableCategories = collectSelectableCategories(categories)

        return selectableCategories.map((categoryItemValue) => (
            <button
                type="button"
                key={categoryItemValue._id || categoryItemValue.slug || categoryItemValue.name}
                className={`categoryContainer__item${activeSlug === categoryItemValue.slug ? ' active' : ''}`}
                onClick={() => editChildrenCategoryIndex(categoryItemValue)}
                title={categoryItemValue.name}
            >
                <span className="categoryContainer__thumb" aria-hidden="true">
                    {categoryItemValue.categoryImage ? (
                        <img
                            src={generatePublicUrl(categoryItemValue.categoryImage)}
                            alt=""
                            loading="lazy"
                        />
                    ) : (
                        <span className="categoryContainer__thumbFallback">
                            {String(categoryItemValue.name || '?').trim().charAt(0).toUpperCase()}
                        </span>
                    )}
                </span>

                <span className="categoryContainer__itemText">
                    {categoryItemValue.name}
                </span>
            </button>
        ))
    }

    const renderVariant = () => {
        if (variantRender === 'productCategory') {
            return filteredCategories.length > 0
                ? renderProductCategories(filteredCategories)
                : null
        }

        if (variantRender === 'allCategory') {
            return (
                <CategoryList
                    editChildrenCategoryIndex={editChildrenCategoryIndex}
                    category={filteredCategories}
                    categoryItem={categoryItem}
                    setCategoryItem={setCategoryItem}
                />
            )
        }

        return null
    }

    const nothingFound = searchQuery && filteredCategories.length === 0

    return (
        <section
            className={[
                'categoryContainer',
                'categoryContainer--catalog',
                hideToggle ? 'categoryContainer--external-toggle' : '',
                collapsed ? 'is-collapsed' : 'is-expanded',
            ].filter(Boolean).join(' ')}
        >
            {!hideToggle ? (
                <div className="categoryContainer__header">
                    <button
                        type="button"
                        className="categoryContainer__toggle"
                        onClick={() => setCollapsed((value) => !value)}
                        aria-expanded={!collapsed}
                        aria-controls="admin-category-browser"
                    >
                        <span
                            className={`categoryContainer__chevron ${collapsed ? '' : 'rot'}`}
                            aria-hidden="true"
                        >
                            ▾
                        </span>
                        <span>Категории</span>
                        <span className="categoryContainer__count">{totalCount}</span>
                    </button>

                    {!collapsed && searchQuery ? (
                        <span className="categoryContainer__resultCount">
                            Найдено: {visibleSelectableCount}
                        </span>
                    ) : null}
                </div>
            ) : null}

            <div id="admin-category-browser" className="categoryContainer__viewport">
                <div className="categoryContainer__toolbar">
                    <label className="categoryContainer__search">
                        <span className="categoryContainer__searchIcon" aria-hidden="true">⌕</span>
                        <input
                            type="search"
                            value={categorySearch}
                            onChange={(event) => setCategorySearch(event.target.value)}
                            placeholder="Поиск категорий"
                            aria-label="Поиск категорий"
                            autoComplete="off"
                        />

                        {categorySearch ? (
                            <button
                                type="button"
                                className="categoryContainer__searchClear"
                                onClick={() => setCategorySearch('')}
                                aria-label="Очистить поиск категорий"
                                title="Очистить"
                            >
                                ×
                            </button>
                        ) : null}
                    </label>

                    <span className="categoryContainer__toolbarHint">
                        {searchQuery
                            ? `${visibleSelectableCount} совпадений`
                            : `${totalCount} категорий`}
                    </span>
                </div>

                <div className="categoryContainer__linkContainer">
                    {variantRender === 'productCategory' ? (
                        <NavLink
                            to="/products/all"
                            onClick={handleShowAll}
                            className={`categoryContainer__item categoryContainer__item--all${activeSlug === 'all' ? ' active' : ''}`}
                        >
                            <span
                                className="categoryContainer__thumb categoryContainer__thumb--all"
                                aria-hidden="true"
                            >
                                #
                            </span>
                            <span className="categoryContainer__itemText">Все</span>
                        </NavLink>
                    ) : null}

                    {nothingFound ? (
                        <div className="categoryContainer__empty">
                            <strong>Категории не найдены</strong>
                            <span>Попробуй изменить поисковый запрос.</span>
                        </div>
                    ) : (
                        renderVariant()
                    )}
                </div>
            </div>
        </section>
    )
}

export default CategoryContainer
